import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Users, Shield, Search, UserPlus, Info, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CommanServices } from '@/services/commanServices';
import { loadAssignUsers } from './AssignPersonnelModal';

// Create a new team (optionally with a first crew) or add a crew to an
// existing team — used from the incident Participants widget so responders
// can be organised on the fly. Everything is saved to the Teams & Crews
// module (db.teams): the module is the single reference collection.
//
// Members picked here are stored on the crew the same way the Crew Staff
// modal stores them, so counts and notifications resolve identically.
const memberToRule = (userId, index) => ({
  escalated_to: String(userId),
  escalation_trigger: 'Verification',
  response_time: 'Immediate',
  response_time_custom: null,
  order_index: index,
  auto_trigger_action_card_id: null,
  is_active: true,
});

const CreateTeamCrewDialog = ({ teams = [], incident = null, onClose, onCreated }) => {
  const [mode, setMode] = useState('team'); // team | crew
  const [teamName, setTeamName] = useState('');
  const [firstCrewName, setFirstCrewName] = useState('');
  const [targetTeamId, setTargetTeamId] = useState(teams[0]?.id || '');
  const [crewName, setCrewName] = useState('');
  const [saving, setSaving] = useState(false);

  // Crew members picker
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]); // [{ id, name, teamLeader, crewLeader }]
  const [memberQuery, setMemberQuery] = useState('');
  const [memberListOpen, setMemberListOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { display } = await loadAssignUsers();
        setUsers(Array.isArray(display) ? display : []);
      } catch (e) { /* picker degrades to empty list */ }
    })();
  }, []);

  const memberIds = useMemo(() => new Set(members.map((m) => String(m.id))), [members]);
  const filteredUsers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    return users
      .filter((u) => u?.id && !memberIds.has(String(u.id)))
      .filter((u) => !q || String(u.name || '').toLowerCase().includes(q))
      .slice(0, 30);
  }, [users, memberIds, memberQuery]);

  const addMember = (u) => {
    setMembers((prev) => [...prev, { id: String(u.id), name: u.name, teamLeader: false, crewLeader: false }]);
    setMemberQuery('');
    setMemberListOpen(false);
  };
  const removeMember = (id) => setMembers((prev) => prev.filter((m) => m.id !== id));
  const toggleMemberFlag = (id, key) => setMembers((prev) => prev.map((member) => member.id === id ? { ...member, [key]: !member[key] } : member));

  const audit = async (detail) => {
    if (!incident?.id) return;
    try {
      await CommanServices.addIncidentAuditLog(incident.id, { action: 'Created', detail });
    } catch (e) { /* audit is best-effort */ }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const rules = members.map((m, i) => memberToRule(m.id, i));
      const memberNote = members.length ? ` with ${members.length} member(s)` : '';

      if (mode === 'team') {
        const name = teamName.trim();
        if (!name) { toast.error('Team name is required'); return; }
        if (members.length && !firstCrewName.trim()) {
          toast.error('Members live in a crew — enter a first crew name to hold them');
          return;
        }
        if (!firstCrewName.trim()) { toast.error('First crew name is required'); return; }
        const crews = firstCrewName.trim()
          ? [{ crew_name: firstCrewName.trim(), is_primary_crew: true, escalation_rules: rules, leaders: members.filter((m) => m.crewLeader).map((m) => m.id), description: '', is_active: true }]
          : [];
        const res = await CommanServices.addteams({
          team_name: name,
          description: incident?.id ? `Created from incident ${incident.id}` : '',
          trigger_conditions: [],
          crews,
          leaders: members.filter((m) => m.teamLeader).map((m) => m.id),
        });
        if (res?.status === false) { toast.error(res?.message || 'Failed to create team'); return; }
        toast.success(`Team "${name}" created — it's now available in the Teams & Crews module`);
        await audit(`Created team '${name}'${firstCrewName.trim() ? ` with crew '${firstCrewName.trim()}'${memberNote}` : ''} from the incident`);
      } else {
        const name = crewName.trim();
        const team = teams.find((t) => String(t.id) === String(targetTeamId));
        if (!team) { toast.error('Pick a team for the new crew'); return; }
        if (!name) { toast.error('Crew name is required'); return; }
        const res = await CommanServices.addTeamCrew(team.id, {
          crew_name: name,
          is_primary_crew: (team.crews || []).length === 0,
          escalation_rules: rules,
          leaders: members.filter((m) => m.crewLeader).map((m) => m.id),
          description: incident?.id ? `Created from incident ${incident.id}` : '',
          is_active: true,
        });
        if (res?.status === false) { toast.error(res?.message || 'Failed to create crew'); return; }
        toast.success(`Crew "${name}" added to "${team.team_name}"`);
        await audit(`Created crew '${name}'${memberNote} under team '${team.team_name}' from the incident`);
      }
      onCreated && (await onCreated());
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const membersDisabled = mode === 'team' && !firstCrewName.trim();

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4" data-testid="create-team-crew-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-[460px] max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900">Create response group</h3>
            <p className="mt-0.5 text-xs text-gray-500">Build a reusable team and crew record.</p>
          </div>
          <button onClick={onClose} data-testid="create-team-crew-close"><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="mode-new-team"
            onClick={() => setMode('team')}
            className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2.5 text-xs font-bold transition-colors ${mode === 'team' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            <Users className="h-4 w-4" /> Team + first crew
          </button>
          <button
            type="button"
            data-testid="mode-new-crew"
            onClick={() => setMode('crew')}
            disabled={teams.length === 0}
            title={teams.length === 0 ? 'No teams yet — create a team first' : undefined}
            className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2.5 text-xs font-bold transition-colors disabled:opacity-50 ${mode === 'crew' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            <Shield className="h-4 w-4" /> Crew in a team
          </button>
        </div>

        {mode === 'team' ? (
          <>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">1. Team name</label>
              <Input
                autoFocus
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
                placeholder="e.g. Evacuation Team"
                className="mt-1"
                data-testid="new-team-name"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">2. First crew name</label>
              <Input
                value={firstCrewName}
                onChange={(e) => setFirstCrewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
                placeholder="e.g. Crew A"
                className="mt-1"
                data-testid="new-team-first-crew"
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Team</label>
              <select
                value={targetTeamId}
                onChange={(e) => setTargetTeamId(e.target.value)}
                className="w-full mt-1 text-sm border border-gray-300 rounded-md text-gray-900 bg-white px-2.5 py-2"
                data-testid="new-crew-team"
              >
                <option value="">Select a team…</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.team_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Crew name</label>
              <Input
                autoFocus
                value={crewName}
                onChange={(e) => setCrewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
                placeholder="e.g. Night Shift Crew"
                className="mt-1"
                data-testid="new-crew-name"
              />
            </div>
          </>
        )}

        {/* Crew members — optional, added straight onto the new crew */}
        <div className="relative">
          <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
            <UserPlus className="h-3.5 w-3.5 text-indigo-500" />
            {mode === 'team' ? '3. Members and leaders (optional)' : 'Members and crew leaders (optional)'}
          </label>

          {members.length > 0 && (
            <div className="mt-2 space-y-1.5" data-testid="new-crew-member-chips">
              {members.map((m) => (
                <div key={m.id} className="flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50/60 px-2.5 py-2 text-xs text-indigo-900">
                  <span className="min-w-0 flex-1 truncate font-semibold">{m.name}</span>
                  {mode === 'team' && <button type="button" role="switch" aria-checked={m.teamLeader} onClick={() => toggleMemberFlag(m.id, 'teamLeader')} className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[9px] font-bold uppercase ${m.teamLeader ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-indigo-200 bg-white text-gray-500'}`}><Crown className="h-3 w-3" /> Team leader</button>}
                  <button type="button" role="switch" aria-checked={m.crewLeader} onClick={() => toggleMemberFlag(m.id, 'crewLeader')} className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[9px] font-bold uppercase ${m.crewLeader ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-indigo-200 bg-white text-gray-500'}`}><Crown className="h-3 w-3" /> Crew leader</button>
                  <button type="button" onClick={() => removeMember(m.id)} className="text-indigo-400 hover:text-red-600" data-testid={`remove-member-${m.id}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="relative mt-1.5">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <input
              data-testid="new-crew-member-search"
              value={memberQuery}
              disabled={membersDisabled}
              onChange={(e) => { setMemberQuery(e.target.value); setMemberListOpen(true); }}
              onFocus={() => setMemberListOpen(true)}
              onBlur={() => setTimeout(() => setMemberListOpen(false), 150)}
              placeholder={membersDisabled ? 'Enter a first crew name to add members' : 'Search users to add as members…'}
              autoComplete="off"
              className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white pl-8 pr-3 py-2 disabled:bg-gray-50 disabled:text-gray-400"
            />
          </div>
          {memberListOpen && !membersDisabled && (
            <div className="absolute z-50 mt-1 w-full max-h-44 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg" data-testid="new-crew-member-list">
              {filteredUsers.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400">{memberQuery ? 'No matching users.' : 'No more users to add.'}</div>
              ) : filteredUsers.map((u) => (
                <button key={u.id} type="button" data-testid={`add-member-${u.id}`}
                  onMouseDown={(ev) => ev.preventDefault()} onClick={() => addMember(u)}
                  className="w-full text-left px-3 py-2 text-sm font-medium text-gray-800 hover:bg-indigo-50">
                  {u.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* How this interacts with the Participants widget — important to understand */}
        <div className="flex gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5" data-testid="create-team-crew-hint">
          <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-blue-800 leading-relaxed">
            Saved to <span className="font-bold">Teams &amp; Crews</span>. Creating it does not involve, assign, notify,
            or grant incident access to members or leaders. It appears here only after this incident targets or assigns it.
          </p>
        </div>

        <Button onClick={save} disabled={saving} className="w-full" data-testid="create-team-crew-save">
          {saving ? 'Saving…' : mode === 'team' ? 'Create team and crew' : 'Add crew'}
        </Button>
      </div>
    </div>,
    document.body
  );
};

export default CreateTeamCrewDialog;
